import React, { useState, useEffect } from 'react';
import './UserDailyLogViewer.css';

const UserDailyLogViewer = ({ csvUrl }) => {
  const [csvData, setCsvData] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedDay, setSelectedDay] = useState('');
  const [selectedUser, setSelectedUser] = useState('');
  const [filteredData, setFilteredData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [availableYears, setAvailableYears] = useState([]);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [availableDays, setAvailableDays] = useState([]);

  useEffect(() => {
    const fetchCsvData = async () => {
      try {
        setLoading(true);
        const response = await fetch(csvUrl);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const csvText = await response.text();
        
        // CSVの改行文字を正規化
        const cleanedCsvText = csvText.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
        
        // 行ごとに分割
        const lines = cleanedCsvText.split('\n').filter(line => line.trim() !== '');
        
        if (lines.length > 0) {
          const headers = lines[0].split(',').map(header => header.trim());
          const data = lines.slice(1).map((line, lineIndex) => {
            try {
              const values = line.split(',').map(value => value.trim());
              const row = {};
              headers.forEach((header, index) => {
                let fieldValue = values[index] || '';
                // 備考欄の場合、改行文字を半角スペースに置換
                if (header === '備考') {
                  fieldValue = fieldValue.replace(/\n/g, ' ').replace(/\r/g, ' ');
                }
                row[header] = fieldValue;
              });
              return row;
            } catch (error) {
              console.error(`行 ${lineIndex + 1} の解析エラー:`, error, line);
              return null;
            }
          }).filter(row => row !== null); // nullの行を除外
          
          setHeaders(headers);
          setCsvData(data);
          setFilteredData(data);
          
          // データの検証とログ出力
          console.log('CSVデータ読み込み完了:', {
            totalRows: data.length,
            headers: headers,
            sampleData: data.slice(0, 3),
            firstFewLines: lines.slice(0, 5)
          });
          
          // 利用可能な年・月・日を抽出
          extractAvailableDates(data);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (csvUrl) {
      fetchCsvData();
    }
  }, [csvUrl]);

  const extractAvailableDates = (data) => {
    const years = new Set();
    const months = new Set();
    const days = new Set();

    data.forEach(row => {
      if (row['日付']) {
        const dateParts = row['日付'].split('-');
        if (dateParts.length === 3) {
          years.add(dateParts[0]);
          months.add(dateParts[1]);
          days.add(dateParts[2]);
        }
      }
    });

    setAvailableYears(Array.from(years).sort());
    setAvailableMonths(Array.from(months).sort());
    setAvailableDays(Array.from(days).sort());
  };

  // 対象者リストを取得（重複削除）
  const getUserList = () => {
    const users = csvData
      .map(row => row['名前'] || '')
      .filter(name => {
        // 基本的なフィルタリング
        const cleanName = name.trim();
        return cleanName !== '' && 
               cleanName.length > 0 && 
               cleanName.length < 50; // 異常に長い名前を除外
      });
    
    console.log('対象者リスト生成:', {
      totalUsers: users.length,
      uniqueUsers: [...new Set(users)].length,
      sampleUsers: users.slice(0, 5)
    });
    
    return [...new Set(users)].sort();
  };

  useEffect(() => {
    let filtered = csvData;

    if (selectedYear) {
      filtered = filtered.filter(row => 
        row['日付'] && row['日付'].startsWith(selectedYear)
      );
    }

    if (selectedMonth) {
      filtered = filtered.filter(row => 
        row['日付'] && row['日付'].includes(`-${selectedMonth}-`)
      );
    }

    if (selectedDay) {
      filtered = filtered.filter(row => 
        row['日付'] && row['日付'].endsWith(`-${selectedDay}`)
      );
    }

    // 対象者選択フィルター
    if (selectedUser) {
      filtered = filtered.filter(row => 
        (row['名前'] || '') === selectedUser
      );
    }

    // 日付で昇順ソート
    const sortedFiltered = filtered.sort((a, b) => {
      const dateA = a['日付'] || '';
      const dateB = b['日付'] || '';
      return dateA.localeCompare(dateB);
    });
    
    setFilteredData(sortedFiltered);
    setCurrentPage(1); // フィルター変更時にページをリセット
  }, [selectedYear, selectedMonth, selectedDay, selectedUser, csvData]);

  const handleYearChange = (year) => {
    setSelectedYear(year);
    setSelectedMonth('');
    setSelectedDay('');
  };

  const handleMonthChange = (month) => {
    setSelectedMonth(month);
    setSelectedDay('');
  };

  const clearFilters = () => {
    setSelectedYear('');
    setSelectedMonth('');
    setSelectedDay('');
    setSelectedUser('');
  };

  const formatTime = (timeStr) => {
    if (!timeStr || timeStr === '') return '-';
    return timeStr;
  };

  const getAttendanceStatus = (workTime, absenceBonus, mealBonus, outsideWork) => {
    if (workTime === '~' || workTime === '') return '欠席';
    if (outsideWork === '1') return '施設外就労';
    if (mealBonus === '100') return '食事加算';
    return '出勤';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case '出勤': return '#28a745';
      case '施設外就労': return '#17a2b8';
      case '食事加算': return '#ffc107';
      case '欠席': return '#dc3545';
      default: return '#6c757d';
    }
  };

  const getAbsenceBonusText = (bonus) => {
    if (!bonus || bonus === '') return '-';
    return bonus === '1' ? 'あり' : 'なし';
  };

  const getMealBonusText = (bonus) => {
    if (!bonus || bonus === '') return '-';
    return bonus === '100' ? '100円' : bonus;
  };

  const getOutsideWorkText = (outside) => {
    if (!outside || outside === '') return '-';
    return outside === '1' ? 'あり' : 'なし';
  };

  // ページネーション計算
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  if (loading) {
    return <div className="user-daily-log-loading">ケース記録データを読み込み中...</div>;
  }

  if (error) {
    return <div className="user-daily-log-error">エラー: {error}</div>;
  }

  return (
    <div className="user-daily-log-viewer">
      <div className="log-header">
        <h2>📊 ケース記録</h2>
        <div className="date-filter">
          <div className="filter-group">
            <label htmlFor="year-select">年:</label>
            <select
              id="year-select"
              value={selectedYear}
              onChange={(e) => handleYearChange(e.target.value)}
            >
              <option value="">全期間</option>
              {availableYears.map(year => (
                <option key={year} value={year}>{year}年</option>
              ))}
            </select>
          </div>
          
          <div className="filter-group">
            <label htmlFor="month-select">月:</label>
            <select
              id="month-select"
              value={selectedMonth}
              onChange={(e) => handleMonthChange(e.target.value)}
              disabled={!selectedYear}
            >
              <option value="">全月</option>
              {availableMonths.map(month => (
                <option key={month} value={month}>{month}月</option>
              ))}
            </select>
          </div>
          
          <div className="filter-group">
            <label htmlFor="day-select">日:</label>
            <select
              id="day-select"
              value={selectedDay}
              onChange={(e) => setSelectedDay(e.target.value)}
              disabled={!selectedMonth}
            >
              <option value="">全日</option>
              {availableDays.map(day => (
                <option key={day} value={day}>{day}日</option>
              ))}
            </select>
          </div>
          
                     <div className="filter-group">
             <label htmlFor="user-select">対象者:</label>
             <select
               id="user-select"
               value={selectedUser}
               onChange={(e) => setSelectedUser(e.target.value)}
             >
               <option value="">全対象者</option>
               {getUserList().map((user, index) => (
                 <option key={index} value={user}>{user}</option>
               ))}
             </select>
           </div>
          
          
          
                     <button 
             className="clear-filter-button"
             onClick={clearFilters}
             disabled={!selectedYear && !selectedMonth && !selectedDay && !selectedUser}
           >
             フィルタークリア
           </button>
        </div>
      </div>

             <div className="log-summary">
         <div className="summary-card">
           <p>総記録数: {filteredData.length}件</p>
           <p>対象期間: {
             selectedYear 
               ? `${selectedYear}年${selectedMonth ? selectedMonth + '月' : ''}${selectedDay ? selectedDay + '日' : ''}`
               : '全期間'
           }</p>
           <p>対象者: {selectedUser || '全対象者'}</p>
         </div>
       </div>

             <div className="log-content">
         {currentItems.map((row, index) => {
          const status = getAttendanceStatus(row['作業時間'], row['欠席時加算'], row['食事加算'], row['施設外就労']);
          return (
            <div key={index} className="daily-log-card">
              <div className="card-header">
                <div className="date-info">
                  <span className="date">{row['日付'] || '日付不明'}</span>
                  <span className="user-name">{row['名前'] || '名前不明'}</span>
                </div>
                <div className="status-badge" style={{backgroundColor: getStatusColor(status)}}>
                  {status}
                </div>
              </div>
              
              <div className="card-body">
                <div className="time-info">
                  <div className="time-item">
                    <span className="label">作業時間:</span>
                    <span className="value">{formatTime(row['作業時間'])}</span>
                  </div>
                  <div className="time-item">
                    <span className="label">申告時間:</span>
                    <span className="value">{formatTime(row['申告時間'])}</span>
                  </div>
                  <div className="time-item">
                    <span className="label">予定:</span>
                    <span className="value">{formatTime(row['予定'])}</span>
                  </div>
                </div>
                
                <div className="bonus-info">
                  <div className="bonus-item">
                    <span className="label">欠席時加算:</span>
                    <span className="value">{getAbsenceBonusText(row['欠席時加算'])}</span>
                  </div>
                  <div className="bonus-item">
                    <span className="label">食事加算:</span>
                    <span className="value">{getMealBonusText(row['食事加算'])}</span>
                  </div>
                  <div className="bonus-item">
                    <span className="label">施設外就労:</span>
                    <span className="value">{getOutsideWorkText(row['施設外就労'])}</span>
                  </div>
                </div>
                
                {row['備考'] && (
                  <div className="notes-section">
                    <span className="label">備考:</span>
                    <p className="notes-text">{row['備考']}</p>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

             {filteredData.length === 0 && (
         <div className="no-data">
           <p>選択された期間のデータがありません。</p>
         </div>
       )}

       {/* ページネーション */}
       {totalPages > 1 && (
         <div className="pagination">
           <div className="pagination-info">
             <span>
               {indexOfFirstItem + 1} - {Math.min(indexOfLastItem, filteredData.length)} / {filteredData.length} 件
             </span>
           </div>
           <div className="pagination-controls">
             <button
               className="pagination-button"
               onClick={() => handlePageChange(currentPage - 1)}
               disabled={currentPage === 1}
             >
               ← 前へ
             </button>
             
             {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
               let pageNumber;
               if (totalPages <= 5) {
                 pageNumber = i + 1;
               } else if (currentPage <= 3) {
                 pageNumber = i + 1;
               } else if (currentPage >= totalPages - 2) {
                 pageNumber = totalPages - 4 + i;
               } else {
                 pageNumber = currentPage - 2 + i;
               }
               
               return (
                 <button
                   key={pageNumber}
                   className={`pagination-button ${currentPage === pageNumber ? 'active' : ''}`}
                   onClick={() => handlePageChange(pageNumber)}
                 >
                   {pageNumber}
                 </button>
               );
             })}
             
             <button
               className="pagination-button"
               onClick={() => handlePageChange(currentPage + 1)}
               disabled={currentPage === totalPages}
             >
               次へ →
             </button>
           </div>
         </div>
       )}

             <div className="log-footer">
         <div className="footer-stats">
           <span>📊 総レコード数: {csvData.length}</span>
           <span>🔍 表示中: {filteredData.length}</span>
           <span>📄 ページ: {currentPage} / {totalPages}</span>
         </div>
       </div>
    </div>
  );
};

export default UserDailyLogViewer;